import type { GattCharacteristic, GattServer } from '@/ble/types';
import { subscribe, tryGetChar, tryGetService, writeChar } from '@/ble/gatt';
import { UUID } from '@/ble/uuids';
import { WriteQueue } from '@/ble/writeQueue';
import type { AnyTrainer, SimParams } from '@/trainer/types';
import { BleTrainerBase } from '../bleTrainerBase';
import { CrankCadence, parseCyclingPower } from '../cps/protocol';
import {
  describeControlCommand,
  encodeRequestControl,
  encodeSetTargetPower,
  encodeSimParams,
  encodeStartResume,
  FTMS_OP,
  FTMS_RESULT,
  FTMS_RESULT_NAMES,
  parseControlResponse,
  parseFeature,
  parseIndoorBikeData,
  parsePowerRange,
  parseStatus,
} from './protocol';

/** Rate limits (SPEC §4.1): sim/grade ≤ 4 Hz, ERG ≤ 2 Hz. */
export const SIM_MIN_INTERVAL_MS = 250;
export const ERG_MIN_INTERVAL_MS = 500;

export class FtmsTrainer extends BleTrainerBase implements AnyTrainer {
  readonly kind = 'ftms' as const;
  private control: GattCharacteristic | null = null;
  private queue: WriteQueue | null = null;
  private crank = new CrankCadence();
  private ibdHasCadence = false;
  private cpsCadenceActive = false;
  private firstIbdAt = 0;
  private hasControl = false;
  /** Commands that failed with "control not permitted" and are retried once after re-requesting control. */
  private retried = new Set<string>();

  protected async setup(server: GattServer): Promise<void> {
    const ftms = await server.getPrimaryService(UUID.FTMS);
    this.hasControl = false;
    this.ibdHasCadence = false;
    this.cpsCadenceActive = false;
    this.firstIbdAt = 0;
    this.crank.reset();

    const feature = await tryGetChar(ftms, UUID.FTMS_FEATURE);
    if (feature) {
      try {
        const f = parseFeature(await feature.readValue());
        this.caps = { ...this.caps, erg: f.powerTarget, sim: f.simulation };
        // Some firmwares report 0 target flags although control works; be permissive.
        if (!f.powerTarget && !f.simulation) this.caps = { ...this.caps, erg: true, sim: true };
        this.log(
          '--',
          null,
          `features machine=0x${f.machine.toString(16)} target=0x${f.target.toString(16)}`,
        );
      } catch {
        /* keep defaults */
      }
    }
    const range = await tryGetChar(ftms, UUID.FTMS_SUPPORTED_POWER_RANGE);
    if (range) {
      try {
        const r = parsePowerRange(await range.readValue());
        if (r.max > r.min) this.caps = { ...this.caps, powerRange: [r.min, r.max] };
      } catch {
        /* optional */
      }
    }

    const ibd = await ftms.getCharacteristic(UUID.FTMS_INDOOR_BIKE_DATA);
    this.unsubs.push(await subscribe(ibd, (dv) => this.onIndoorBikeData(dv)));

    const status = await tryGetChar(ftms, UUID.FTMS_STATUS);
    if (status) {
      try {
        this.unsubs.push(await subscribe(status, (dv) => this.onStatus(dv)));
      } catch {
        /* optional */
      }
    }

    this.control = await ftms.getCharacteristic(UUID.FTMS_CONTROL_POINT);
    const control = this.control;
    this.queue = new WriteQueue({
      awaitResponse: true,
      responseTimeoutMs: 1500,
      send: async (bytes) => {
        this.log(
          'tx',
          bytes,
          describeControlCommand(new DataView(bytes.buffer, bytes.byteOffset, bytes.length)),
        );
        await writeChar(control, bytes, true);
      },
      onLog: (m) => this.log('--', null, m),
    });
    this.unsubs.push(await subscribe(control, (dv) => this.onControlResponse(dv)));

    await this.requestControl();
    void this.queue.enqueue('start', encodeStartResume(), { responseKey: FTMS_OP.START_RESUME });

    // Cadence fallback via Cycling Power crank data on the same device (KICKR exposes CPS too).
    const cps = await tryGetService(server, UUID.CPS);
    if (cps) {
      const meas = await tryGetChar(cps, UUID.CPS_MEASUREMENT);
      if (meas) {
        try {
          this.unsubs.push(await subscribe(meas, (dv) => this.onCyclingPower(dv)));
        } catch {
          /* optional */
        }
      }
    }
  }

  protected onTransportLost(): void {
    this.queue?.clear();
    this.queue = null;
    this.control = null;
    this.hasControl = false;
  }

  async requestControl(): Promise<boolean> {
    if (!this.queue) return false;
    const r = await this.queue.enqueue('control', encodeRequestControl(), {
      responseKey: FTMS_OP.REQUEST_CONTROL,
      priority: 10,
    });
    this.hasControl = r.ok;
    if (!r.ok) this.log('--', null, `request control failed (${r.reason})`);
    return r.ok;
  }

  async setErg(watts: number): Promise<void> {
    await this.sendControl('erg', encodeSetTargetPower(watts), FTMS_OP.SET_TARGET_POWER, ERG_MIN_INTERVAL_MS);
  }

  async setSim(p: SimParams): Promise<void> {
    await this.sendControl('sim', encodeSimParams(p), FTMS_OP.SET_SIM_PARAMS, SIM_MIN_INTERVAL_MS);
  }

  get controlGranted(): boolean {
    return this.hasControl;
  }

  private async sendControl(
    key: string,
    bytes: Uint8Array,
    op: number,
    minIntervalMs: number,
  ): Promise<void> {
    if (!this.queue) return;
    const r = await this.queue.enqueue(key, bytes, { responseKey: op, minIntervalMs });
    if (
      !r.ok &&
      r.reason === 'rejected' &&
      r.code === FTMS_RESULT.CONTROL_NOT_PERMITTED &&
      !this.retried.has(key)
    ) {
      this.retried.add(key);
      const granted = await this.requestControl();
      if (granted && this.queue) await this.queue.enqueue(key, bytes, { responseKey: op });
      this.retried.delete(key);
    }
  }

  private onIndoorBikeData(dv: DataView): void {
    const d = parseIndoorBikeData(dv);
    const now = Date.now();
    if (!this.firstIbdAt) this.firstIbdAt = now;
    const patch: Record<string, number> = {};
    if (d.speedKmh !== undefined) patch.speedKmh = d.speedKmh;
    if (d.powerW !== undefined) patch.powerW = Math.max(0, d.powerW);
    if (d.cadenceRpm !== undefined) {
      this.ibdHasCadence = true;
      patch.cadenceRpm = d.cadenceRpm;
    } else if (this.cpsCadenceActive) {
      patch.cadenceRpm = this.crank.current(now);
    }
    if (d.heartRateBpm !== undefined && d.heartRateBpm > 0) patch.hrBpm = d.heartRateBpm;
    if (!this.ibdHasCadence && !this.cpsCadenceActive && now - this.firstIbdAt > 3000) {
      this.cpsCadenceActive = true;
      this.log('--', null, 'IBD has no cadence → using Cycling Power crank data');
    }
    this.emitData(patch);
  }

  private onCyclingPower(dv: DataView): void {
    const m = parseCyclingPower(dv);
    if (m.crankRevs === undefined || m.lastCrankEventTime === undefined) return;
    const cad = this.crank.update(m.crankRevs, m.lastCrankEventTime, Date.now());
    if (this.cpsCadenceActive && !this.ibdHasCadence) this.emitData({ cadenceRpm: cad });
  }

  private onControlResponse(dv: DataView): void {
    const r = parseControlResponse(dv);
    if (!r) return;
    this.log(
      'rx',
      dv,
      `response op=0x${r.requestOp.toString(16)} ${FTMS_RESULT_NAMES[r.result] ?? r.result}`,
    );
    if (r.requestOp === FTMS_OP.REQUEST_CONTROL) this.hasControl = r.ok;
    this.queue?.handleResponse(r.requestOp, r.ok, r.result);
  }

  private onStatus(dv: DataView): void {
    const s = parseStatus(dv);
    this.log('rx', dv, `status ${s.kind}`);
    if (s.kind === 'controlPermissionLost') {
      this.hasControl = false;
      void this.requestControl();
    }
  }
}
